import api from './api';
import type {
  ConversationResponse,
  MessageResponse,
  InboxCountsResponse,
  ConversationListParams,
  MessageListParams,
  ContactInfo,
  SessionResponse,
} from '@/types/chat.types';

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor?: string;
    limit: number;
  };
}

export interface ConversationDetailResponse {
  conversation: ConversationResponse;
  contact?: ContactInfo;
}

export async function getConversations(
  params?: ConversationListParams
): Promise<{ data: ConversationResponse[]; nextCursor?: string }> {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.set('status', params.status);
  if (params?.assignedTo) queryParams.set('assignedTo', params.assignedTo);
  if (params?.search) queryParams.set('search', params.search);
  if (params?.limit) queryParams.set('limit', String(params.limit));
  if (params?.cursor) queryParams.set('cursor', params.cursor);

  const query = queryParams.toString();
  const url = query ? `/conversations?${query}` : '/conversations';
  const response = await api.get<PaginatedResponse<ConversationResponse>>(url);

  return {
    data: response.data.data,
    nextCursor: response.data.pagination?.nextCursor,
  };
}

export async function getConversation(id: string): Promise<ConversationDetailResponse> {
  const response = await api.get<{ data: ConversationDetailResponse }>(`/conversations/${id}`);
  return response.data.data;
}

export async function getMessages(
  conversationId: string,
  params?: MessageListParams
): Promise<{ data: MessageResponse[]; nextCursor?: string }> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.set('limit', String(params.limit));
  if (params?.cursor) queryParams.set('cursor', params.cursor);
  if (params?.order) queryParams.set('order', params.order);

  const query = queryParams.toString();
  const url = query
    ? `/conversations/${conversationId}/messages?${query}`
    : `/conversations/${conversationId}/messages`;
  const response = await api.get<PaginatedResponse<MessageResponse>>(url);

  return {
    data: response.data.data,
    nextCursor: response.data.pagination?.nextCursor,
  };
}

export async function sendMessage(
  conversationId: string,
  text: string,
  replyToMessageId?: string
): Promise<MessageResponse> {
  const response = await api.post<{ data: MessageResponse }>(
    `/conversations/${conversationId}/messages`,
    { text, replyToMessageId }
  );
  return response.data.data;
}

export type MediaMessageType = 'image' | 'video' | 'document';

export async function sendMediaMessage(
  conversationId: string,
  mediaUrl: string,
  mimeType: string,
  options?: {
    caption?: string;
    type?: MediaMessageType;
    filename?: string;
    replyToMessageId?: string;
  }
): Promise<MessageResponse> {
  const type = options?.type ?? 'image';
  const response = await api.post<{ data: MessageResponse }>(
    `/conversations/${conversationId}/messages`,
    {
      type,
      mediaUrl,
      mimeType,
      caption: options?.caption,
      filename: options?.filename,
      replyToMessageId: options?.replyToMessageId,
    }
  );
  return response.data.data;
}

export async function sendAudioMessage(
  conversationId: string,
  mediaUrl: string,
  mimeType: string,
  duration?: number,
  replyToMessageId?: string
): Promise<MessageResponse> {
  const response = await api.post<{ data: MessageResponse }>(
    `/conversations/${conversationId}/messages`,
    { type: 'audio', mediaUrl, mimeType, duration, replyToMessageId }
  );
  return response.data.data;
}

export async function sendStickerMessage(
  conversationId: string,
  mediaUrl: string,
  mimeType: string,
  replyToMessageId?: string
): Promise<MessageResponse> {
  const response = await api.post<{ data: MessageResponse }>(
    `/conversations/${conversationId}/messages`,
    { type: 'sticker', mediaUrl, mimeType, replyToMessageId }
  );
  return response.data.data;
}

/** Destino de encaminhamento: conversa existente ou contato (abre/reusa conversa). */
export interface ForwardTarget {
  conversationId?: string;
  contactId?: string;
}

export interface ForwardResult {
  conversationId: string;
  contactName?: string;
  messageId?: string;
  status: 'sent' | 'failed';
  error?: string;
}

export async function forwardMessage(
  conversationId: string,
  messageId: string,
  targets: ForwardTarget[]
): Promise<ForwardResult[]> {
  const response = await api.post<{ data: { results: ForwardResult[] } }>(
    `/conversations/${conversationId}/messages/${messageId}/forward`,
    { targets }
  );
  return response.data.data.results;
}

export async function createConversation(contactId: string): Promise<ConversationResponse> {
  const response = await api.post<{ data: ConversationResponse }>('/conversations', { contactId });
  return response.data.data;
}

export async function assignConversation(
  conversationId: string,
  userId: string
): Promise<ConversationResponse> {
  const response = await api.post<{ data: ConversationResponse }>(
    `/conversations/${conversationId}/assign`,
    { userId }
  );
  return response.data.data;
}

export async function closeConversation(
  conversationId: string,
  reason?: string
): Promise<ConversationResponse> {
  const response = await api.post<{ data: ConversationResponse }>(
    `/conversations/${conversationId}/close`,
    { reason }
  );
  return response.data.data;
}

export async function reactToMessage(
  conversationId: string,
  messageId: string,
  emoji: string | null
): Promise<MessageResponse> {
  const response = await api.put<{ data: MessageResponse }>(
    `/conversations/${conversationId}/messages/${messageId}/reaction`,
    { emoji }
  );
  return response.data.data;
}

export async function listSessions(conversationId: string): Promise<SessionResponse[]> {
  const response = await api.get<{ data: SessionResponse[] }>(
    `/conversations/${conversationId}/sessions`
  );
  return response.data.data;
}

export async function getInboxCounts(): Promise<InboxCountsResponse> {
  const response = await api.get<{ data: InboxCountsResponse }>('/conversations/counts');
  return response.data.data;
}

export interface Sticker {
  id: string;
  url: string;
  mimeType: string;
  createdAt: string;
}

export async function saveSticker(url: string, mimeType?: string): Promise<void> {
  await api.post('/conversations/stickers', { url, mimeType });
}

export async function deleteSticker(stickerId: string): Promise<void> {
  await api.delete(`/conversations/stickers/${stickerId}`);
}

export async function listStickers(
  limit?: number,
  cursor?: string
): Promise<{ data: Sticker[]; nextCursor?: string }> {
  const queryParams = new URLSearchParams();
  if (limit) queryParams.set('limit', String(limit));
  if (cursor) queryParams.set('cursor', cursor);

  const query = queryParams.toString();
  const url = query ? `/conversations/stickers?${query}` : '/conversations/stickers';
  const response = await api.get<PaginatedResponse<Sticker>>(url);

  return {
    data: response.data.data,
    nextCursor: response.data.pagination?.nextCursor,
  };
}

export const chatQueryKeys = {
  all: ['conversations'] as const,
  lists: () => [...chatQueryKeys.all, 'list'] as const,
  list: (params?: ConversationListParams) => [...chatQueryKeys.lists(), params] as const,
  details: () => [...chatQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...chatQueryKeys.details(), id] as const,
  messages: (conversationId: string) => [...chatQueryKeys.all, 'messages', conversationId] as const,
  sessions: (conversationId: string) =>
    [...chatQueryKeys.all, 'sessions', conversationId] as const,
  counts: () => [...chatQueryKeys.all, 'counts'] as const,
  stickers: () => ['stickers'] as const,
};
